// src/components/MAP/hooks/useTrajectoryHighlight.js
import { useState, useEffect, useCallback } from 'react';

/**
 * 管理轨迹高亮状态和视频切换的 Hook
 * @param {Object} options - 配置选项
 * @param {Object} options.mapInstance - 地图实例引用
 * @param {Object} options.backgroundPolylinesRef - 背景轨迹线引用
 * @param {Object} options.markersRef - 标记引用
 * @param {Object} options.robotPositionUpdatersRef - 机器人位置更新器引用
 * @param {number|null} options.activeRobotId - 当前活跃的机器人 ID
 * @param {string} options.timeframe - 当前时间框架 ("11AM" 或 "11PM")
 * @param {Function} options.onRobotSelect - 机器人选择回调函数
 * @returns {Object} 高亮状态和控制函数
 */
export const useTrajectoryHighlight = ({
  mapInstance,
  backgroundPolylinesRef,
  markersRef,
  robotPositionUpdatersRef,
  activeRobotId,
  timeframe = "11AM",
  onRobotSelect
}) => {
  const [highlightedTrajectory, setHighlightedTrajectory] = useState(null);

  /**
   * 高亮指定轨迹
   * @param {number} robotId - 要高亮的机器人 ID
   * @param {Object} options - 可选参数
   * @param {boolean} options.updateState - 是否更新全局状态，默认为 true
   * @param {boolean} options.triggerCallback - 是否触发选择回调，默认为 false
   * @param {number} options.time - 当前视频时间，默认为 null
   */
  const highlightTrajectory = useCallback((robotId, options = { updateState: true, triggerCallback: false, time: null }) => {
    if (!mapInstance.current) return;
    
    // 转换为基于 0 的索引
    const highlightIndex = robotId - 1;
    
    // 更新内部高亮状态
    if (options.updateState) {
      setHighlightedTrajectory(highlightIndex);
    }
    
    // 应用视觉高亮效果
    Object.entries(backgroundPolylinesRef.current).forEach(([index, polyline]) => {
      if (!polyline) return;
      const routeIndex = parseInt(index);
      
      try {
        if (routeIndex === highlightIndex) {
          // 高亮选中轨迹
          if (polyline._path && polyline._path.classList) {
            polyline._path.classList.add('highlighted-trajectory');
            polyline.setStyle({
              weight: 10,
              opacity: 0.6
            });
          }

          // 高亮标记
          const marker = markersRef.current[index];
          if (marker && marker._icon) {
            const img = marker._icon.querySelector("img");
            if (img && img.classList) {
              img.classList.add("highlighted-marker");
            }
          }
        } else {
          // 移除其他轨迹高亮
          if (polyline._path && polyline._path.classList) {
            polyline._path.classList.remove('highlighted-trajectory');
            polyline.setStyle({
              weight: 8,
              opacity: 0.3
            });
          }
          
          // 移除其他标记高亮
          const marker = markersRef.current[index];
          if (marker && marker._icon) {
            const img = marker._icon.querySelector("img");
            if (img && img.classList) {
              img.classList.remove("highlighted-marker");
            }
          }
        }
      } catch (e) {
        console.warn("Error applying trajectory highlight:", e);
      }
    });
    
    // 如果需要，触发选择回调
    if (options.triggerCallback && onRobotSelect) {
      const timePart = timeframe === "11AM" ? "day" : "night";
      const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
      
      // 调用回调函数并传递时间参数（如果有）
      if (options.time !== null && options.time !== undefined) {
        onRobotSelect(robotId, videoUrl, options.time);
      } else {
        onRobotSelect(robotId, videoUrl);
      }
    }

    // 更新机器人位置
// 更新机器人位置
if (options.time !== null && options.time !== undefined && robotPositionUpdatersRef && robotPositionUpdatersRef.current) {
  const robotPositionUpdater = robotPositionUpdatersRef.current[highlightIndex];
  if (robotPositionUpdater) {
    robotPositionUpdater(options.time);
  }
}
  }, [mapInstance, backgroundPolylinesRef, markersRef, onRobotSelect, timeframe, robotPositionUpdatersRef]);

  /**
   * 清除所有轨迹高亮
   */
  const clearHighlight = useCallback(() => {
    if (!mapInstance.current) return;
    
    Object.entries(backgroundPolylinesRef.current).forEach(([index, polyline]) => {
      if (!polyline) return;
      
      try {
        // 移除轨迹高亮
        if (polyline._path && polyline._path.classList) {
          polyline._path.classList.remove('highlighted-trajectory');
          polyline.setStyle({
            weight: 8,
            opacity: 0.3
          });
        }
        
        // 移除标记高亮
        const marker = markersRef.current[index];
        if (marker && marker._icon) {
          const img = marker._icon.querySelector("img");
          if (img && img.classList) {
            img.classList.remove("highlighted-marker");
          }
        }
      } catch (e) {
        console.warn("Error clearing trajectory highlight:", e);
      }
    });
    
    // 更新状态
    setHighlightedTrajectory(null);
  }, [mapInstance, backgroundPolylinesRef, markersRef]);

  /**
   * 添加轨迹和标记的点击事件
   */
  const setupTrajectoryClickHandlers = useCallback(() => {
    if (!mapInstance.current || !onRobotSelect) return;
    
    // 获取当前应用状态
    let currentTime = 0;
    try {
      // 尝试从应用状态获取当前时间
      const appStore = window.useAppStore ? window.useAppStore.getState() : null;
      if (appStore && appStore.videoState) {
        currentTime = appStore.videoState.currentTime || 0;
      }
    } catch (e) {
      console.warn("Error getting current time from app store:", e);
    }
    
    Object.entries(backgroundPolylinesRef.current).forEach(([index, polyline]) => {
      if (!polyline) return;
      
      const routeIndex = parseInt(index);
      const robotId = routeIndex + 1;
      
      // 移除任何现有的点击处理程序以防止重复
      polyline.off('click');
      
      // 添加新的点击处理程序 - 修改：传递当前时间
      polyline.on('click', () => {
        // 再次获取最新的时间，确保准确性
        try {
          const appStore = window.useAppStore ? window.useAppStore.getState() : null;
          if (appStore && appStore.videoState) {
            currentTime = appStore.videoState.currentTime || 0;
          }
        } catch (e) {
          // 如果获取失败，使用默认时间
        }
        
        highlightTrajectory(robotId, { 
          updateState: true, 
          triggerCallback: true,
          time: currentTime
        });
      });
      
      // 为对应的标记添加点击处理程序 - 修改：传递当前时间
      const marker = markersRef.current[index];
      if (marker) {
        marker.off('click');
        marker.on('click', () => {
          // 再次获取最新的时间，确保准确性
          try {
            const appStore = window.useAppStore ? window.useAppStore.getState() : null;
            if (appStore && appStore.videoState) {
              currentTime = appStore.videoState.currentTime || 0;
            }
          } catch (e) {
            // 如果获取失败，使用默认时间
          }
          
          highlightTrajectory(robotId, { 
            updateState: true, 
            triggerCallback: true,
            time: currentTime
          });
        });
      }
    });
  }, [mapInstance, backgroundPolylinesRef, markersRef, onRobotSelect, highlightTrajectory]);

  // 初始化点击处理程序
  useEffect(() => {
    setupTrajectoryClickHandlers();
    
    // 组件卸载时清理
    return () => {
      Object.values(backgroundPolylinesRef.current).forEach(polyline => {
        if (polyline) polyline.off('click');
      });
      
      Object.values(markersRef.current).forEach(marker => {
        if (marker) marker.off('click');
      });
    };
  }, [setupTrajectoryClickHandlers, backgroundPolylinesRef, markersRef]);

  // 监听 activeRobotId 变化，自动更新高亮
  useEffect(() => {
    if (activeRobotId) {
      highlightTrajectory(activeRobotId, { updateState: true, triggerCallback: false });
    } else {
      clearHighlight();
    }
  }, [activeRobotId, highlightTrajectory, clearHighlight]);

  return {
    highlightedTrajectory,
    highlightTrajectory,
    clearHighlight,
    setupTrajectoryClickHandlers
  };
};